import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Row, Col } from "deltoid/core/Blocks/BootstrapContainers";
import { H } from "deltoid/core/Blocks/Typography";
import Form from "deltoid/core/Form";
import SingleFileUploader from "./SingleFileUploader";
import { toastActions, uiActions, dialogActions, dialogSelectors, manualUploadActions } from "@store/ducks";
import ConfirmationDialog from "./ConfirmationDialog";

const UploadFrontandBack = ({
  dialogProps,
  closeDialog,
  uploadFile,
  removeFile,
  uploadMergeDocs,
  loadUI,
  showToast,
}) => {
  const {
    modalTitle,
    modalSubTitle,
    documentCategoryId,
    documentTypeId,
    frontDocName,
    backDocName,
    maxFileSizeLimit,
    optional,
    uploadedDocument,
    setUploadedFile,
  } = dialogProps;

  const [frontSide, setFrontSide] = useState(
    uploadedDocument && uploadedDocument.uploaded_documents
      ? uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("FRONT"))
      : null
  );
  const [backSide, setBackSide] = useState(
    uploadedDocument && uploadedDocument.uploaded_documents
      ? uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("BACK"))
      : null
  );
  const [frontSideUploaderState, setFrontSideUploaderState] = useState(
    uploadedDocument &&
      uploadedDocument.uploaded_documents &&
      uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("FRONT"))
      ? "uploaded"
      : "initial"
  );
  const [backSideUploaderState, setBackSideUploaderState] = useState(
    uploadedDocument &&
      uploadedDocument.uploaded_documents &&
      uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("BACK"))
      ? "uploaded"
      : "initial"
  );
  const [frontSideFileUUID, setFrontSideFileUUID] = useState(
    uploadedDocument &&
      uploadedDocument.uploaded_documents &&
      uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("FRONT"))
      ? uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("FRONT")).kyc_document_uuid
      : null
  );
  const [backSideFileUUID, setBackSideFileUUID] = useState(
    uploadedDocument &&
      uploadedDocument.uploaded_documents &&
      uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("BACK"))
      ? uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("BACK")).kyc_document_uuid
      : null
  );
  const [frontSideFileID, setFrontSideFileID] = useState(
    uploadedDocument &&
      uploadedDocument.uploaded_documents &&
      uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("FRONT"))
      ? uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("FRONT")).id
      : null
  );
  const [backSideFileID, setBackSideFileID] = useState(
    uploadedDocument &&
      uploadedDocument.uploaded_documents &&
      uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("BACK"))
      ? uploadedDocument.uploaded_documents.find((doc) => doc.filename.includes("BACK")).id
      : null
  );

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitBtnDisabled, setSubmitBtnDisabled] = useState(optional?.length === 0 && (!frontSide || !backSide) ? true :
    optional?.[0] === "front" && !backSide ? true : optional?.[0] === "back" && !frontSide ? true : false);

  useEffect(() => {
    setSubmitBtnDisabled(optional?.length === 0 && (!frontSide || !backSide) ? true :
      optional[0] === "front" && !backSide ? true : optional[0] === "back" && !frontSide ? true : false);
  }, [frontSide, backSide]);

  const handleFrontSideUpload = ([frontSideFile]) => {
    setFrontSide(frontSideFile);
    let fileData = new FormData();
    const renamedFrontSideFile = new File([frontSideFile], `FRONT_${frontSideFile.name}`, { type: frontSideFile.type });
    fileData.append("uploaded_documents", renamedFrontSideFile);
    fileData.append("document_category_id", documentCategoryId);
    fileData.append("document_type_id", documentTypeId);
    setFrontSideUploaderState("uploading");

    uploadFile(fileData, (attachment) => {
      if (attachment) {
        setFrontSideUploaderState("uploaded");
        setFrontSideFileUUID(attachment.kyc_document_uuid);
        setFrontSideFileID(attachment.id);
        showToast("success", "Success", `${frontSideFile.name} was uploaded successfully.`);
      } else {
        showToast(
          "error",
          "Error",
          `${frontSideFile.name} could not be uploaded. Please remove the file and try again.`
        );
        setFrontSideUploaderState("uploadError");
        setFrontSide(null);
        setFrontSideFileUUID(null);
        setFrontSideFileID(null);
      }
    });
  };

  const handleBackSideUpload = ([backSideFile]) => {
    setBackSide(backSideFile);
    let fileData = new FormData();
    const renamedBackSideFile = new File([backSideFile], `BACK_${backSideFile.name}`, { type: backSideFile.type });
    fileData.append("uploaded_documents", renamedBackSideFile);
    fileData.append("document_category_id", documentCategoryId);
    fileData.append("document_type_id", documentTypeId);
    setBackSideUploaderState("uploading");

    uploadFile(fileData, (attachment) => {
      if (attachment) {
        setBackSideUploaderState("uploaded");
        setBackSideFileUUID(attachment.kyc_document_uuid);
        setBackSideFileID(attachment.id);
        showToast("success", "Success", `${backSideFile.name} was uploaded successfully.`);
      } else {
        showToast(
          "error",
          "Error",
          `${backSideFile.name} could not be uploaded. Please remove the file and try again.`
        );
        setBackSideUploaderState("uploadError");
        setBackSide(null);
        setBackSideFileUUID(null);
        setBackSideFileID(null);
      }
    });
  };

  const handleFrontFileRejected = (files) => {
    showToast("warning", "Warning", `The file was rejected. Please try again.`);
    setFrontSideUploaderState("uploadError");
    return;
  };

  const handleBackFileRejected = (files) => {
    showToast("warning", "Warning", `The file was rejected. Please try again.`);
    setBackSideUploaderState("uploadError");
    return;
  };

  const mergeAttachments = () => {
    const docId = frontSideFileUUID;
    let fileData = [];
    if (frontSideFileID && backSideFileID) fileData.push(frontSideFileID, backSideFileID);
    else if (frontSideFileID && !backSideFileID) fileData.push(frontSideFileID);
    else if (!frontSideFileID && backSideFileID) fileData.push(backSideFileID);
    setLoading(true);
    uploadMergeDocs(fileData, docId, (response) => {
      if (response) {
        showToast("success", "Success", `The documents were uploaded successfully.`);
        setLoading(false);
        setUploadedFile(response);
        closeDialog();
      }
      else {
        setLoading(false);
        toastActions.show({
          variant: "error",
          title: "Error",
          content: "We encountered an error. Could not upload the files. Please try again.",
        });
      }
    });
  };

  const removeFrontAttachment = (callback) => {
    if (frontSideFileUUID) {
      const fileData = [frontSideFileID];
      setFrontSideUploaderState("uploading");
      removeFile(fileData, (response) => {
        if (response) {
          showToast("success", "Success", `The file was removed successfully.`);
          setFrontSide(null);
          setFrontSideUploaderState("initial");
          setFrontSideFileUUID(null);
          setFrontSideFileID(null);
          if (callback) callback("initial");
        } else {
          if (callback) callback("uploaded");
          setFrontSideUploaderState("uploaded");
        }
      });
    }
  };
  const removeBackAttachment = (callback) => {
    if (backSideFileUUID) {
      const fileData = [backSideFileID];
      setBackSideUploaderState("uploading");
      removeFile(fileData, (response) => {
        if (response) {
          showToast("success", "Success", `The file was removed successfully.`);
          setBackSide(null);
          setBackSideUploaderState("initial");
          setBackSideFileUUID(null);
          setBackSideFileID(null);
          if (callback) callback("initial");
        } else {
          if (callback) callback("uploaded");
          setBackSideUploaderState("uploaded");
        }
      });
    }
  };

  const submitCancelConfirmation = () => {
    setIsConfirmationOpen(false);
    if (frontSide) removeFrontAttachment(); else setFrontSideUploaderState("initial");
    if (backSide) removeBackAttachment(); else setBackSideUploaderState("initial");
  };

  return (
    <>
      {isConfirmationOpen && (
        <ConfirmationDialog
          onNegativeClick={() => {
            setIsConfirmationOpen(false);
          }}
          onPositiveClick={() => {
            submitCancelConfirmation();
          }}
        />
      )}
      {!isConfirmationOpen && (
        <>
          <div className="mod-content-wrap" data-testid="frontAndBackUpload">
            <div className="mod-top-title-wrap">
              <H level={5} variant="bold" className="modal-heading">
                {modalTitle}
              </H>
              <p className="modal-subtext"> {modalSubTitle}</p>
            </div>
            <div className="mod-two-upload-form">
              <Row className="mod-uploader-row">
                <Col md={4}>{frontSideImage}</Col>
                <Col md={8}>
                  <Form.Label id={`labelFrontSide`} className="loader-label">
                    {frontDocName}
                  </Form.Label>
                  <div className="uploader-div">
                    <SingleFileUploader
                      onFileDropAccepted={handleFrontSideUpload}
                      onFileDropRejected={handleFrontFileRejected}
                      allowedFileExtensions="image/png, application/pdf, image/jpg, image/jpeg"
                      maxFileSizeInMB={maxFileSizeLimit}
                      onRemove={removeFrontAttachment}
                      disabled={false}
                      multiple={false}
                      uploadedDocument={frontSide}
                      uploaderState={frontSideUploaderState}
                      onUploadIconClick={null}
                      resetSingleFileUploader={{}}
                    />
                  </div>
                  <div className="upload-item-description">jpg,jpeg, pdf, png less than 5 MB</div>
                </Col>
              </Row>
              <Row className="mod-uploader-row">
                <Col md={4}>{backSideImage}</Col>
                <Col md={8}>
                  <Form.Label id={`labelBackSide`} className="loader-label">
                    {backDocName}
                  </Form.Label>
                  <div className="uploader-div">
                    <SingleFileUploader
                      onFileDropAccepted={handleBackSideUpload}
                      onFileDropRejected={handleBackFileRejected}
                      allowedFileExtensions="image/png, application/pdf, image/jpg, image/jpeg"
                      maxFileSizeInMB={maxFileSizeLimit}
                      onRemove={removeBackAttachment}
                      disabled={false}
                      multiple={false}
                      uploadedDocument={backSide}
                      uploaderState={backSideUploaderState}
                      onUploadIconClick={null}
                      resetSingleFileUploader={{}}
                    />
                  </div>
                  <div className="upload-item-description">jpg,jpeg, pdf, png less than 5 MB</div>
                </Col>
              </Row>
            </div>
          </div>
          <div className="mod-btn-bot-row">
            <Form.Button
              variant="underlined"
              className="btn-cancel"
              disabled={false}
              onClick={() => {
                if (!frontSide && !backSide) closeDialog();
                else setIsConfirmationOpen(true);
              }}
            >
              Cancel Upload
            </Form.Button>
            <Form.Button
              variant="default"
              loading={loading}
              className="btn-upload"
              onClick={mergeAttachments}
              disabled={submitBtnDisabled}
            >
              {loading ? "Uploading..." : "Upload"}
            </Form.Button>
          </div>
        </>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  openDialog: () => dispatch(dialogActions.open("two-file-upload")),
  closeDialog: () => dispatch(dialogActions.close()),
  uploadFile: (fileData, callback) => dispatch(manualUploadActions.appendAttachment(fileData, callback)),
  removeFile: (fileData, callback) => dispatch(manualUploadActions.removeAttachment(fileData, callback)),
  uploadMergeDocs: (fileData, docId, callback) =>
    dispatch(manualUploadActions.mergeAttachments(fileData, docId, callback)),
  loadUI: () => dispatch(uiActions.loadUI({})),
  showToast: (variant, title, content) =>
    dispatch(toastActions.show({ variant: variant, title: title, content: content })),
});
const mapStateToProps = (state) => ({
  dialogProps: dialogSelectors.getProps(state),
});
export default connect(mapStateToProps, mapDispatchToProps)(UploadFrontandBack);

const frontSideImage = (
  <svg width="135" height="82" viewBox="0 0 135 82" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_i)">
      <rect x="1" y="1" width="133" height="80" rx="5.73684" fill="white" />
    </g>
    <rect x="0.5" y="0.5" width="134" height="81" rx="6.23684" stroke="#231F20" />
    <rect width="68.9043" height="3" rx="1.5" transform="matrix(1 0 -0.005328 -0.999986 58.0273 17)" fill="#B8EBDF" />
    <rect width="24" height="3" rx="1.5" transform="matrix(1 0 -0.005328 -0.999986 98 74)" fill="#B8EBDF" />
    <rect width="68.9043" height="3" rx="1.5" transform="matrix(1 0 -0.005328 -0.999986 58.0293 24)" fill="#B8EBDF" />
    <rect width="24" height="3" rx="1.5" transform="matrix(1 0 -0.005328 -0.999986 68.0254 74)" fill="#B8EBDF" />
    <path
      d="M66.3262 33.9941H119.944"
      stroke="#50C3B6"
      stroke-width="1.55413"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M66.002 50.9941H119.619"
      stroke="#50C3B6"
      stroke-width="1.55413"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M66.3262 37.8799H101.294"
      stroke="#50C3B6"
      stroke-width="0.777065"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M66.3262 54.8799H101.294"
      stroke="#50C3B6"
      stroke-width="0.777065"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <circle cx="61.6635" cy="33.9936" r="1.55413" fill="#18AF8B" />
    <circle cx="61.6635" cy="50.9936" r="1.55413" fill="#18AF8B" />
    <path
      d="M23.8377 19.6161C23.8568 19.1572 24.2267 18.7173 24.4459 18.3393C24.5775 18.1137 24.7085 17.8886 24.8391 17.6641C25.0419 18.8154 25.2328 19.9697 25.4118 21.1269L24.8227 20.6856C24.6281 20.5385 24.4295 20.4031 24.2377 20.256C23.939 20.0354 23.8199 20.0369 23.8377 19.6161Z"
      fill="white"
    />
    <path
      d="M32.1746 57.7978C32.3664 57.5036 32.4185 57.1108 32.4048 56.5783C32.3924 56.0929 32.3842 55.6059 32.3787 55.1205C32.3404 51.74 32.1463 48.3674 31.7965 45.0027C31.622 43.3227 31.4092 41.6482 31.1581 39.9791C30.9841 38.8214 30.8252 37.5886 30.2457 36.5692C29.6662 35.5498 28.7565 35.1099 27.7071 34.9437C25.448 34.5877 23.0697 34.9099 20.8915 35.4541C20.2065 35.6248 19.4557 35.7483 18.8283 36.1043C17.7021 36.7501 17.172 38.1829 16.8363 39.4304C16.3911 41.0926 16.2541 42.8505 16.0993 44.5672C15.9303 46.4531 15.8166 48.3444 15.7581 50.2411C15.7075 51.8592 15.6759 53.4891 15.7143 55.1146C15.7349 55.9266 15.7718 56.7328 15.8335 57.5477C15.8636 57.9287 15.7458 58.4745 16.0006 58.7834C16.5144 59.4056 17.5199 59.2527 18.2077 59.31C20.227 59.4792 22.2121 59.6263 24.2356 59.5027C26.606 59.378 28.959 58.9995 31.2595 58.373C31.7157 58.2479 32.0088 58.0611 32.1746 57.7978Z"
      fill="#231F20"
    />
    <path
      d="M16.4852 58.2004C16.4706 58.1802 16.4586 58.1579 16.4496 58.1342C16.4149 58.029 16.3991 57.9176 16.403 57.8061C16.3249 54.5272 16.2784 51.26 16.4592 47.9825C16.6072 45.3229 16.7209 42.5308 17.336 39.9388C17.6799 38.4869 18.2484 36.9085 19.6965 36.4348C22.3885 35.5522 25.2709 34.9976 28.0533 35.6743C29.4808 36.02 30.0576 37.3174 30.3371 38.7385C30.9358 41.7909 31.2221 44.9595 31.4591 48.0649C31.6892 51.0835 31.7673 54.0962 31.781 57.1294C31.781 57.6855 30.3028 57.8061 29.9138 57.8885C29.3055 58.0165 28.6945 58.1297 28.0835 58.2445C25.5465 58.7177 22.9677 58.8805 20.3965 58.7299C19.7572 58.6936 19.1151 58.6363 18.4703 58.5578C17.9538 58.496 17.1141 58.5358 16.6483 58.318C16.5851 58.2952 16.5288 58.2545 16.4852 58.2004Z"
      fill="#18AF8B"
    />
    <path
      d="M28.0026 35.9065C27.0875 33.9353 25.338 32.9571 23.3104 33.1115C21.4158 33.2586 19.5608 33.9942 19.0512 36.1434C18.4963 38.497 18.8251 41.2199 19.317 43.5486C19.3309 43.6012 19.3573 43.6489 19.3936 43.6872C19.4299 43.7255 19.4749 43.753 19.5241 43.7669C19.5733 43.7809 19.6251 43.7808 19.6743 43.7668C19.7235 43.7528 19.7684 43.7252 19.8047 43.6869C20.3335 43.0022 21.0917 42.5679 21.9158 42.4777C22.3417 42.4391 22.7703 42.4957 23.1748 42.6439C23.7228 42.841 23.9598 43.3044 24.5147 42.994C24.9133 42.7704 25.2709 42.4939 25.6928 42.3159C26.5148 41.9687 27.4738 41.929 27.8478 42.9587C27.9382 43.2088 28.3643 43.3015 28.4095 42.9587C28.7219 40.6153 29.0274 38.1101 28.0026 35.9065Z"
      fill="#231F20"
    />
    <path
      d="M26.7343 41.4789C25.7863 41.3759 24.9397 41.9202 24.1834 42.4762C23.514 42.0072 22.7192 41.7883 21.9222 41.8535C21.1252 41.9186 20.3704 42.2642 19.7748 42.8366C19.4076 40.9846 19.3069 39.083 19.4762 37.1981C19.583 36.1463 19.8872 35.1048 20.7557 34.5061C21.4942 33.9986 22.4545 33.8118 23.3149 33.7412C25.189 33.5941 26.7398 34.4899 27.5508 36.3287C28.3331 38.1131 28.1879 40.1329 27.9495 42.0467C27.6152 41.7165 27.1872 41.5165 26.7343 41.4789Z"
      fill="white"
    />
    <path
      d="M23.564 44.6161L22.8761 42.8924C22.7857 42.666 22.9525 42.4199 23.1963 42.4199H25.2594C25.4946 42.4199 25.6608 42.6503 25.5866 42.8734L25.0133 44.5971C24.9664 44.7379 24.8346 44.833 24.6862 44.833H23.8842C23.7432 44.833 23.6163 44.7471 23.564 44.6161Z"
      fill="#231F20"
    />
    <path
      d="M23.4448 44.952L22.7492 49.4335C22.7168 49.6424 22.8784 49.8311 23.0899 49.8311H25.4923C25.7049 49.8311 25.8668 49.6405 25.8325 49.4307L25.1003 44.9493C25.0731 44.7826 24.929 44.6602 24.7601 44.6602H23.7855C23.6155 44.6602 23.4709 44.7841 23.4448 44.952Z"
      fill="#231F20"
    />
    <path
      d="M18.0963 32.7008C18.512 32.7723 18.9372 32.6667 19.2814 32.4066C19.4693 32.262 19.6284 32.0784 19.7491 31.8667C19.8698 31.6549 19.9497 31.4193 19.9842 31.1738C20.0211 30.9135 20.0099 30.6478 19.9511 30.3921C19.8923 30.1364 19.7872 29.8957 19.6417 29.6837C19.388 29.3202 19.0241 29.0632 18.6128 28.957C18.5635 28.9452 18.5142 28.9349 18.4635 28.9261C18.0376 28.8578 17.603 28.9588 17.2415 29.21C17.0337 29.3566 16.8704 29.5652 16.7716 29.8102C16.6863 30.0341 16.6398 30.2729 16.6346 30.5148C16.6175 30.991 16.7247 31.4628 16.9442 31.877C17.0659 32.1009 17.2335 32.292 17.4344 32.4357C17.6352 32.5795 17.864 32.6722 18.1032 32.7067L18.0963 32.7008Z"
      fill="#231F20"
    />
    <path
      d="M17.3883 31.583C17.4692 31.7276 17.578 31.8521 17.7073 31.9481C17.8365 32.0441 17.9834 32.1095 18.138 32.1398C18.2926 32.1701 18.4515 32.1648 18.604 32.1241C18.7565 32.0833 18.8993 32.0082 19.0226 31.9037C19.5336 31.4624 19.598 30.7268 19.2679 30.1487C19.0934 29.8341 18.8159 29.6007 18.4897 29.4941C18.3256 29.4505 18.1546 29.4463 17.9888 29.4819C17.823 29.5175 17.6666 29.592 17.5307 29.7C17.3923 29.8109 17.2833 29.9586 17.2146 30.1287C17.1458 30.2988 17.1198 30.4852 17.1389 30.6695C17.1516 30.9912 17.2372 31.3048 17.3883 31.583Z"
      fill="white"
    />
    <path
      d="M28.5253 32.4C28.9409 32.4715 29.3661 32.3659 29.7103 32.1058C29.8981 31.961 30.057 31.7773 30.1777 31.5656C30.2983 31.3539 30.3784 31.1185 30.4131 30.8731C30.4501 30.6127 30.4388 30.3471 30.3801 30.0914C30.3213 29.8357 30.2161 29.5949 30.0706 29.3829C29.8169 29.0194 29.4531 28.7624 29.0418 28.6562C28.9925 28.6444 28.9432 28.6341 28.8925 28.6253C28.4665 28.557 28.032 28.658 27.6704 28.9092C27.4624 29.0554 27.2989 29.2642 27.2005 29.5094C27.1121 29.7326 27.0633 29.9716 27.0567 30.214C27.0384 30.6902 27.1452 31.1622 27.3649 31.5762C27.4871 31.8 27.655 31.991 27.856 32.1347C28.0571 32.2784 28.286 32.3712 28.5253 32.4059V32.4Z"
      fill="#231F20"
    />
    <path
      d="M27.8179 31.2813C27.8991 31.4258 28.0079 31.5501 28.1372 31.646C28.2665 31.7418 28.4132 31.8071 28.5678 31.8374C28.7223 31.8678 28.8811 31.8625 29.0336 31.8219C29.1861 31.7813 29.3289 31.7064 29.4523 31.602C29.9633 31.1607 30.0277 30.4252 29.6976 29.8471C29.5231 29.5325 29.2456 29.2991 28.9194 29.1925C28.7553 29.1488 28.5842 29.1446 28.4184 29.1802C28.2527 29.2158 28.0962 29.2903 27.9604 29.3984C27.822 29.5092 27.713 29.657 27.6443 29.8271C27.5755 29.9972 27.5494 30.1836 27.5686 30.3678C27.5813 30.6896 27.6669 31.0031 27.8179 31.2813Z"
      fill="white"
    />
    <path
      d="M26.3444 37.0258C26.3567 36.8375 26.3677 36.6492 26.3704 36.4594C26.3766 36.049 26.3629 35.6385 26.3293 35.2296C26.2992 34.8413 26.3293 34.4456 26.3293 34.0528C26.3293 33.8807 26.2924 33.6483 26.3855 33.4982C26.4787 33.3482 26.7006 33.2864 26.839 33.1908C27.0354 33.0515 27.2222 32.8971 27.3979 32.7289C27.7842 32.3647 28.1184 31.9414 28.3898 31.4726C29.257 29.9721 29.6378 28.0274 29.5556 25.5252C29.4803 23.2583 28.9131 19.2482 25.9608 17.8537C25.9266 17.8375 25.8964 17.8257 25.8663 17.814C25.8361 17.8022 25.8046 17.7904 25.7663 17.7713C25.3159 17.535 24.8337 17.3762 24.3374 17.3006C23.4295 17.1682 22.5053 17.2655 21.6385 17.5845C20.2179 18.0949 19.052 19.2791 18.3574 20.9193C17.8218 22.1874 17.5354 23.7143 17.5245 25.4516C17.5026 27.6582 17.8231 29.5043 18.4766 30.946C18.7817 31.6206 19.2153 32.219 19.7493 32.7024C19.932 32.8677 20.1251 33.0191 20.3275 33.1555C20.4439 33.2349 20.448 33.2893 20.4645 33.4717L20.4713 33.5615C20.4859 33.7272 20.4996 33.8934 20.5124 34.0602C20.5371 34.4 20.5549 34.7354 20.5631 35.059C20.5809 35.7241 20.5608 36.3898 20.5028 37.0522C20.5028 37.0773 22.6318 43.819 24.3292 43.2306C25.1512 42.9482 25.8293 41.7816 26.0252 40.8887C26.2513 39.859 26.2773 38.7704 26.3129 37.7157C26.3129 37.4877 26.3293 37.2567 26.3444 37.0258Z"
      fill="#231F20"
    />
    <path
      d="M18.5106 29.4535C18.8654 30.8112 19.5326 31.9542 20.6176 32.6986C20.6761 32.7573 20.7297 32.8213 20.7779 32.8898C20.8152 32.9477 20.8482 33.0087 20.8766 33.0722C20.9313 33.2645 20.9712 33.4613 20.9958 33.6606C20.9968 33.6686 20.9996 33.6762 21.004 33.6827C21.1065 34.7716 21.1138 35.8684 21.0259 36.9587C21.1752 38.2959 23.4425 42.6958 24.3138 42.456C24.9207 42.2898 25.2002 41.4881 25.366 40.9188C25.5872 40.0892 25.7286 39.2374 25.7879 38.3768C25.8386 37.7884 25.8852 37.2132 25.8866 36.6292C25.8866 36.0673 25.8099 35.5112 25.8003 34.9493C25.7907 34.4432 25.7414 33.8828 25.9373 33.3973C26.1565 32.853 26.6675 32.6839 27.062 32.322C27.464 31.9527 27.8037 31.5118 28.0662 31.0187C28.7663 29.6947 28.9868 28.1325 29.0252 26.6364C29.1088 23.8665 28.6032 20.1153 26.1482 18.5957C26.0373 18.5177 25.9989 18.4927 25.9208 18.4412C24.7988 17.7057 23.4549 17.6572 22.1753 17.9999C21.5523 18.1726 20.9672 18.4758 20.4538 18.8921C19.9403 19.3084 19.5085 19.8296 19.1833 20.4257C18.5202 21.6025 18.2037 22.9765 18.0873 24.3387C17.9401 26.0571 18.0835 27.7899 18.5106 29.4535Z"
      fill="white"
    />
    <path
      d="M26.1504 18.5957C26.0394 18.5177 26.001 18.4927 25.9229 18.4412C24.8009 17.7057 23.457 17.6572 22.1774 17.9999C21.5544 18.1726 20.9694 18.4758 20.4559 18.8921C19.9424 19.3084 19.5106 19.8296 19.1854 20.4257C18.5223 21.6025 18.2059 22.9765 18.0894 24.3387C17.964 25.8331 18.0564 27.3394 18.3634 28.8033C19.3167 26.6092 19.7949 24.2117 19.7622 21.7908C22.2136 22.2805 24.7389 22.1291 27.1244 21.3495C27.2664 23.7349 27.8757 26.0613 28.9122 28.1766C28.9788 27.665 29.0191 27.1498 29.0328 26.6335C29.1123 23.8665 28.6067 20.1153 26.1504 18.5957Z"
      fill="#FDEEEE"
    />
    <path
      d="M26.5714 16.0901C24.5726 15.4238 22.2519 15.6635 20.323 16.4608C19.401 16.8418 18.4612 17.3773 18.0091 18.3732C17.8222 18.7589 17.7497 19.1969 17.8014 19.6281C17.853 20.0594 18.0264 20.4633 18.2981 20.7857C18.319 20.8124 18.3447 20.8342 18.3737 20.8499C18.4027 20.8655 18.4343 20.8747 18.4667 20.8769C20.4476 21.5992 22.5766 21.508 24.6316 21.2756C25.5727 21.1682 26.5276 21.0682 27.4619 20.899C28.1469 20.7739 28.8771 20.468 29.1059 19.7001C29.6374 17.9775 27.8798 16.527 26.5714 16.0901Z"
      fill="#231F20"
    />
    <path
      d="M28.2492 20.0016C27.9081 20.2738 27.3998 20.3253 26.9957 20.3826C26.1312 20.5062 25.2626 20.6254 24.3927 20.718C22.4747 20.9225 20.4979 20.9902 18.647 20.3267C17.7853 19.2264 18.784 17.9275 19.7362 17.3847C20.7261 16.8383 21.8038 16.4993 22.9118 16.3858C24.6462 16.1872 27.2395 16.2681 28.2889 18.0613C28.6301 18.6336 28.8588 19.5074 28.2492 20.0016Z"
      fill="#231F20"
    />
    <path
      d="M17.96 20.0013C17.3435 20.2602 17.0503 21.0663 16.8229 21.6739C16.5002 22.5386 16.3234 23.4582 16.301 24.3894C16.2557 26.2209 16.9366 27.8773 17.9559 29.3145L17.9696 29.3307C17.9957 29.3681 18.0297 29.3985 18.0687 29.4195C18.1077 29.4404 18.1507 29.4513 18.1943 29.4513C18.2379 29.4513 18.2809 29.4404 18.3199 29.4195C18.3589 29.3985 18.3928 29.3681 18.4189 29.3307C18.4414 29.3064 18.4584 29.277 18.4686 29.2446C18.4788 29.2122 18.482 29.1777 18.4778 29.1438C18.9561 27.1832 19.215 25.1684 19.2491 23.142C19.2683 22.3226 19.5314 19.3393 17.96 20.0013Z"
      fill="#231F20"
    />
    <path
      d="M18.6422 24.5928C18.5489 25.9001 18.3578 27.1974 18.071 28.4719C16.7284 26.2447 16.29 23.3232 17.7463 20.9696C17.9052 20.5773 18.1911 20.5072 18.6039 20.7592C18.6442 20.9689 18.6663 21.1821 18.6696 21.3962C18.6888 21.6904 18.6957 21.9772 18.7011 22.2656C18.7139 23.0442 18.6943 23.82 18.6422 24.5928Z"
      fill="#231F20"
    />
    <path
      d="M30.5588 23.4244C30.5457 22.6238 30.7198 21.5933 30.3927 20.8737C30.1598 20.3824 29.3782 18.2789 28.948 17.9435C28.6124 17.6817 28.112 19.1864 27.7914 19.5085C27.2681 20.0396 27.5654 21.1443 27.6393 21.7916C27.9038 24.0952 28.3819 26.3592 28.9258 28.604C28.9228 28.6431 28.9293 28.6823 28.9446 28.7179C28.96 28.7535 28.9837 28.7842 29.0134 28.807C29.0485 28.8389 29.0901 28.8613 29.1349 28.8723C29.1797 28.8833 29.2263 28.8826 29.2708 28.8702C29.3152 28.8578 29.3563 28.8341 29.3904 28.801C29.4245 28.768 29.4508 28.7267 29.4669 28.6805C30.056 27.0079 30.6136 25.2323 30.5588 23.4244Z"
      fill="#231F20"
    />
    <path
      d="M28.0836 20.7459C28.0521 20.3502 28.1439 19.5559 28.7028 20.0104C28.859 20.1369 28.9604 20.4135 29.0686 20.5827C29.8148 21.7065 30.1387 23.0913 29.9755 24.4603C29.8492 25.536 29.6074 26.5924 29.2549 27.6084C28.9371 26.2521 28.6494 24.8869 28.4329 23.5086C28.2795 22.5936 28.1562 21.6712 28.0836 20.7459Z"
      fill="#231F20"
    />
    <path
      d="M21.7979 26.0558C21.6513 25.7822 21.3938 25.8543 21.2582 26.0691C21.1686 26.2354 21.1305 26.428 21.1492 26.619C21.1679 26.8101 21.2425 26.9899 21.3623 27.1326C21.3973 27.1747 21.4419 27.2064 21.4916 27.2247C21.5414 27.2429 21.5946 27.2472 21.6463 27.237C21.698 27.2268 21.7465 27.2025 21.7869 27.1665C21.8274 27.1304 21.8585 27.0838 21.8774 27.0311C21.9296 26.8719 21.9495 26.7025 21.9358 26.5343C21.9221 26.3661 21.8751 26.203 21.7979 26.0558Z"
      fill="#231F20"
    />
    <path
      d="M25.8429 25.7684C25.7224 25.4742 25.4593 25.5257 25.3045 25.7243C25.1993 25.8797 25.1426 26.0669 25.1426 26.259C25.1426 26.4511 25.1993 26.6383 25.3045 26.7937C25.3356 26.8393 25.3771 26.8756 25.425 26.8991C25.4729 26.9226 25.5256 26.9325 25.5781 26.9279C25.6305 26.9234 25.681 26.9044 25.7248 26.873C25.7685 26.8415 25.804 26.7985 25.8279 26.7481C25.8946 26.5952 25.9304 26.4288 25.933 26.26C25.9356 26.0911 25.9049 25.9236 25.8429 25.7684Z"
      fill="#231F20"
    />
    <path
      d="M23.905 25.8512C23.8259 25.139 23.2132 25.4399 23.2922 26.1522C23.3459 26.6538 23.6 28.1135 23.2682 28.3843C22.8969 28.6853 23.2188 29.6584 23.5887 29.36C24.313 28.7706 24.0222 26.9146 23.905 25.8512Z"
      fill="#231F20"
    />
    <path
      d="M36.0424 47.6562C35.655 45.4097 34.924 43.2475 33.8792 41.2572C32.7051 39.0153 31.0118 36.6484 29.0363 35.4127C29.0124 35.394 28.9851 35.3809 28.9562 35.3742C28.9273 35.3675 28.8974 35.3674 28.8685 35.374C28.8396 35.3805 28.8122 35.3935 28.7883 35.4121C28.7643 35.4306 28.7443 35.4544 28.7294 35.4819C28.6822 35.5149 28.6478 35.5651 28.6324 35.6232C28.6171 35.6814 28.6218 35.7435 28.6458 35.7982C28.6568 35.8291 28.6691 35.8585 28.6801 35.8894C28.6957 35.8965 28.7108 35.9049 28.7253 35.9144C28.8197 35.9885 28.899 36.0826 28.9582 36.1909C29.1171 36.4351 29.2925 36.6661 29.4514 36.9103C29.493 36.9878 29.5048 37.0798 29.4844 37.1666C29.4639 37.2533 29.4128 37.3281 29.3418 37.3751C29.9405 38.5828 30.5885 39.7803 30.6446 41.1366C30.633 41.1672 30.6252 41.1994 30.6213 41.2322L30.0076 48.4874C27.938 47.8372 25.7128 48.096 23.8208 49.2067C23.7958 49.2114 23.7714 49.2193 23.7482 49.2302L22.4193 49.851C21.4 48.2917 19.9766 46.7618 18.2957 46.0807L18.2628 46.069C18.1614 45.0795 18.0596 44.089 17.9573 43.0975C17.9514 43.0357 17.9296 42.9769 17.8943 42.9278C17.8589 42.8786 17.8115 42.8411 17.7573 42.8194C17.5109 41.5455 17.5593 40.2262 17.8984 38.9771C17.8584 38.9263 17.8327 38.8643 17.8242 38.7984C17.8157 38.7326 17.8248 38.6655 17.8504 38.6049C18.0669 38.0636 18.2751 37.3913 18.7135 36.9868C18.7889 36.7205 18.4642 36.4263 18.2354 36.6675C14.7036 40.3746 12.6513 45.6188 12.739 50.9366C12.7801 53.4183 13.2391 56.2324 15.1228 57.9035C16.8339 59.4172 19.2766 59.5614 21.3274 58.8626C22.7262 58.386 23.9441 57.5387 25.0442 56.5207C26.8813 57.7079 29.0527 58.386 31.1981 57.955C33.0736 57.5769 34.8436 56.3118 35.682 54.4407C36.615 52.3636 36.4177 49.8628 36.0424 47.6562Z"
      fill="#231F20"
    />
    <path
      d="M19.2975 47.3795C20.2994 48.1165 21.1706 49.0396 21.8704 50.1053L18.6852 51.5926C18.6494 51.547 18.6017 51.5142 18.5482 51.4984L18.0344 50.6864C18.0059 50.6429 17.9695 50.6059 17.9276 50.5776C17.8879 50.5275 17.8318 50.4956 17.7709 50.4885C17.7101 50.4814 17.6489 50.4996 17.6001 50.5393C17.4147 50.698 17.202 50.816 16.9741 50.8865C16.7741 50.9277 16.5631 50.8997 16.5809 50.6408C16.5946 50.4437 16.6727 50.4128 16.8275 50.3186C17.2713 50.0599 17.761 49.905 18.2646 49.8641C18.6345 49.8303 18.6523 49.2036 18.2783 49.2389C17.7865 49.2845 16.8891 49.4346 16.3603 49.8523C16.342 49.8398 16.3228 49.829 16.3028 49.82C15.8123 49.6125 16.7439 49.0668 16.885 49.0124C17.2542 48.8761 17.6448 48.8191 18.0344 48.8447C18.4098 48.8623 18.4235 48.2371 18.0481 48.218C17.3768 48.1856 16.5206 48.3224 15.9932 48.827C15.9625 48.7874 15.9235 48.7561 15.8795 48.7358C15.4753 48.549 16.348 48.0003 16.474 47.9414C16.8532 47.7783 17.2653 47.7235 17.67 47.7826C18.0426 47.8296 18.0536 47.203 17.6837 47.1574C17.0215 47.0667 16.3509 47.232 15.7918 47.6237C15.8562 46.8073 16.6932 46.4042 17.4275 46.4822C18.1098 46.5572 18.7359 46.972 19.2975 47.3795Z"
      fill="white"
    />
    <path
      d="M22.3552 57.7319C20.4085 58.794 17.8234 59.0868 15.9164 57.7511C14.8587 57.0155 14.2162 55.8902 13.834 54.6457C13.8505 54.6418 13.8666 54.6359 13.8819 54.628L17.9768 52.6127C17.9939 52.6045 18.01 52.5941 18.0248 52.5818L23.8513 49.8633C23.8995 49.8628 23.9468 49.8486 23.9883 49.8221C24.8804 49.2762 25.867 48.9318 26.8876 48.8102C27.9081 48.6885 28.9411 48.7921 29.923 49.1146H29.9531C29.9449 49.2278 29.9353 49.3411 29.9257 49.4544C29.2862 50.5863 28.5525 51.6537 27.7338 52.6436C27.701 52.6608 27.672 52.6854 27.6488 52.7157C26.109 54.628 24.4815 56.5713 22.3552 57.7319Z"
      fill="white"
    />
    <path
      d="M33.0163 56.5726C30.6298 58.0436 27.7857 57.4979 25.502 56.0798C26.2471 55.3222 26.9554 54.5238 27.624 53.6879H27.6336C28.5785 52.6282 29.4246 51.4719 30.1599 50.2354C31.8748 51.237 33.7009 52.0016 35.5946 52.5111C35.3069 54.1557 34.5548 55.6252 33.0163 56.5726Z"
      fill="white"
    />
    <path
      d="M18.3871 37.3945C16.7245 39.2126 15.4199 41.3716 14.5511 43.7428C13.6824 46.1139 13.2673 48.6487 13.3305 51.1958C13.3491 52.1561 13.464 53.1114 13.673 54.0453L17.076 52.3741C17.1015 52.3455 17.1323 52.323 17.1664 52.3079L18.0967 51.874L17.6857 51.2282C17.3514 51.443 16.976 51.6063 16.5815 51.493C16.3925 51.4429 16.2255 51.3241 16.1089 51.1567C15.9922 50.9894 15.9332 50.7839 15.9417 50.5751C15.9499 50.4943 15.9683 50.4151 15.9965 50.3397C15.8443 50.2327 15.7304 50.0736 15.6732 49.8882C15.616 49.7029 15.6189 49.5022 15.6814 49.3188C15.4978 49.2315 15.354 49.0695 15.2815 48.8684C15.2091 48.6674 15.2139 48.4436 15.295 48.2464C15.2631 48.2042 15.2427 48.1532 15.2361 48.0993C14.9772 46.4547 16.3883 45.7309 17.6733 45.9104L17.3815 43.0801C17.3528 42.7991 17.6309 42.7256 17.8131 42.8433C17.8673 41.1805 18.0095 39.5224 18.2391 37.877C18.2816 37.7182 18.3323 37.5563 18.3871 37.3945Z"
      fill="#18AF8B"
    />
    <path
      d="M35.654 49.1066C35.1142 43.9697 32.5907 39.3889 28.8945 36.0996C29.0507 37.8649 30.1179 39.4168 30.6563 41.1012C30.7933 40.8378 31.2345 40.8908 31.2043 41.2483C30.9732 43.9942 30.7408 46.7401 30.507 49.4861C30.5182 49.5423 30.513 49.6009 30.4919 49.6538C30.4897 49.6702 30.4865 49.6864 30.4823 49.7023C32.1271 50.6566 33.8757 51.3879 35.6882 51.8795C35.7731 50.9567 35.7616 50.0266 35.654 49.1066Z"
      fill="#18AF8B"
    />
    <defs>
      <filter
        id="filter0_i"
        x="-1.43421"
        y="0"
        width="136.434"
        height="83.4342"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feMorphology radius="1.43421" operator="erode" in="SourceAlpha" result="effect1_innerShadow" />
        <feOffset dx="-1.43421" dy="1.43421" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.945098 0 0 0 0 0.945098 0 0 0 0 0.945098 0 0 0 1 0" />
        <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
      </filter>
    </defs>
  </svg>
);
const backSideImage = (
  <svg width="135" height="82" viewBox="0 0 135 82" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_i)">
      <rect x="1" y="1" width="133" height="80" rx="5.73684" fill="white" />
    </g>
    <rect x="0.5" y="0.5" width="134" height="81" rx="6.23684" stroke="#231F20" />
    <path
      d="M83 32C83.3571 33.9048 84.4286 37.1429 85.8571 34.8571C87.2857 32.5714 90.2619 33.9048 91.5714 34.8571L94.4286 32L96.5714 34.8571C96.3333 33.9048 96.2857 32 98 32C99.7143 32 100.381 32.7143 100.5 33.0714"
      stroke="#B8EBDF"
      stroke-width="0.714286"
    />
    <rect width="132.378" height="8" transform="matrix(1 0 -0.005328 -0.999986 2.01562 21)" fill="#B8EBDF" />
    <rect width="120.38" height="4" transform="matrix(1 0 -0.005328 -0.999986 7.04297 66)" fill="#B8EBDF" />
    <rect width="120.38" height="4" transform="matrix(1 0 -0.005328 -0.999986 7.04297 74)" fill="#B8EBDF" />
    <rect width="64.38" height="4" transform="matrix(1 0 -0.005328 -0.999986 7.04297 57)" fill="#B8EBDF" />
    <rect x="106" y="26" width="16" height="15" fill="#B8EBDF" />
    <defs>
      <filter
        id="filter0_i"
        x="-1.43421"
        y="0"
        width="136.434"
        height="83.4342"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feMorphology radius="1.43421" operator="erode" in="SourceAlpha" result="effect1_innerShadow" />
        <feOffset dx="-1.43421" dy="1.43421" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.945098 0 0 0 0 0.945098 0 0 0 0 0.945098 0 0 0 1 0" />
        <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
      </filter>
    </defs>
  </svg>
);
